// ActivationCountdown — shown when user has marked setup complete but is
// not yet verified. Counts down to `scheduledActivationAt` and polls /me
// every 20s so the moment the scheduler flips verified=True, this card
// disappears and the dashboard advances to the plan-picker.
import { useEffect, useState } from "react";
import { Hourglass, Sparkles, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";

function formatTime(seconds) {
  if (seconds <= 0) return "00:00:00";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function ActivationCountdown({ scheduledAt, onRefresh }) {
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!scheduledAt) return undefined;
    const target = new Date(scheduledAt).getTime();
    const tick = () => {
      const diff = Math.max(0, Math.floor((target - Date.now()) / 1000));
      setSecondsLeft(diff);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [scheduledAt]);

  return (
    <section
      className="mb-8 rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 p-8 text-center shadow-md"
      data-testid="activation-countdown"
    >
      <div className="flex justify-center">
        <div className="relative">
          <Hourglass className="h-16 w-16 text-amber-600 animate-pulse" />
          <Sparkles className="absolute -right-2 -top-2 h-6 w-6 text-amber-500 animate-bounce" />
        </div>
      </div>
      <h2 className="mt-4 font-heading text-2xl font-semibold text-amber-900 sm:text-3xl">
        ⏳ Activating your account
      </h2>
      <p className="mt-2 text-sm text-amber-800">
        Setup is 100% complete. Your account will auto-verify in:
      </p>

      <div className="mt-6 inline-flex items-baseline gap-2 rounded-2xl border-2 border-amber-400 bg-white px-8 py-4 font-mono shadow-sm">
        <span className="text-4xl font-bold tabular-nums text-amber-700" data-testid="countdown-timer">
          {formatTime(secondsLeft)}
        </span>
        <span className="text-xs uppercase tracking-[0.16em] text-amber-600">hh:mm:ss</span>
      </div>

      <div className="mt-6 mx-auto max-w-md rounded-xl border border-amber-200 bg-white/70 p-4 text-left">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <ShieldCheck className="h-4 w-4" /> What happens next?
        </h3>
        <ul className="mt-2 space-y-1.5 text-xs text-amber-800">
          <li>✓ Your account will activate automatically (no admin action needed)</li>
          <li>✓ You will be asked to choose a subscription plan</li>
          <li>✓ All reports will unlock based on your plan</li>
          <li>✓ This page refreshes automatically</li>
        </ul>
      </div>

      {onRefresh && (
        <Button variant="outline" onClick={onRefresh} className="mt-6" data-testid="countdown-refresh-btn">
          Check status now
        </Button>
      )}
    </section>
  );
}
